import type { MeResponse, Permission } from "@salon-pos/shared";
import { useEffect } from "react";
import { HIDDEN_ROUTES, NAV } from "../nav";

/** Task K: tiny event bus between pages and the floating AI assistant (open in a mode, refresh after "Apply"). */
export type AiPanelMode = "help" | "website" | "import";
export function openAssistant(mode: AiPanelMode, importId?: string) {
  window.dispatchEvent(new CustomEvent("spos:ai-open", { detail: { mode, importId } }));
}
export function notifyApplied(kind: string) {
  window.dispatchEvent(new CustomEvent("spos:ai-applied", { detail: { kind } }));
}
export function useAiApplied(cb: (kind: string) => void) {
  useEffect(() => {
    const h = (e: Event) => cb(String((e as CustomEvent<{ kind: string }>).detail?.kind ?? ""));
    window.addEventListener("spos:ai-applied", h);
    return () => window.removeEventListener("spos:ai-applied", h);
  }, [cb]);
}

const can = (me: MeResponse, perm?: Permission) => !perm || (me.permissions[perm] ?? "none") !== "none";
/** Deep links from help articles are shown only when this user can open the page (role + add-ons). */
export function linkAllowed(me: MeResponse, link: string): boolean {
  const [path, query] = link.split("?") as [string, string | undefined];
  // Settings → AI (shop's own key) only while the platform switch SHOP_AI_ENABLED is on
  // (the store app has no owner routes; the check is compiled out there so the owner path never ships in the native bundle)
  if (import.meta.env.VITE_CLIENT !== "native" && path === "/owner/settings" && new URLSearchParams(query ?? "").get("tab") === "ai" && !me.shopAi) return false;
  if (path.startsWith("/help/")) return true;
  for (const items of Object.values(NAV)) {
    const it = items.find((i) => i.path === path);
    if (it) return can(me, it.perm) && (!it.feature || me.features.includes(it.feature));
  }
  if (path in HIDDEN_ROUTES) return can(me, HIDDEN_ROUTES[path]);
  if (path === "/setup") return me.membership?.role === "owner";
  return false;
}
